// candlestickPatterns.js
// Utility functions to detect classic candlestick patterns in OHLC arrays

/**
 * Each function below receives (ohlc, index) and returns either null or a string description if the pattern is detected at that index.
 * OHLC format: [open, high, low, close]
 */

function isBullish(bar) {
  return bar[3] > bar[0];
}
function isBearish(bar) {
  return bar[3] < bar[0];
}
function bodySize(bar) {
  return Math.abs(bar[3] - bar[0]);
}
function upperShadow(bar) {
  return bar[1] - Math.max(bar[0], bar[3]);
}
function lowerShadow(bar) {
  return Math.min(bar[0], bar[3]) - bar[2];
}
function isDoji(bar, threshold = 0.1) {
  return bodySize(bar) <= threshold * (bar[1] - bar[2]);
}

export function detectBearishEngulfing(ohlc, i) {
  if (i < 1) return null;
  const prev = ohlc[i - 1], curr = ohlc[i];
  if (
    isBullish(prev) && isBearish(curr) &&
    curr[0] > prev[3] && curr[3] < prev[0] &&
    bodySize(curr) > bodySize(prev)
  ) {
    return 'Bearish Engulfing Pattern';
  }
  return null;
}

export function detectMorningStar(ohlc, i) {
  if (i < 2) return null;
  const [a, b, c] = [ohlc[i - 2], ohlc[i - 1], ohlc[i]];
  if (
    isBearish(a) &&
    (bodySize(b) < bodySize(a) * 0.5 || isDoji(b)) &&
    b[0] < a[3] && b[3] < a[3] &&
    isBullish(c) && c[3] > ((a[0] + a[3]) / 2)
  ) {
    return isDoji(b) ? 'Morning Doji Star' : 'Morning Star';
  }
  return null;
}

export function detectEveningStar(ohlc, i) {
  if (i < 2) return null;
  const [a, b, c] = [ohlc[i - 2], ohlc[i - 1], ohlc[i]];
  if (
    isBullish(a) &&
    (bodySize(b) < bodySize(a) * 0.5 || isDoji(b)) &&
    b[0] > a[3] && b[3] > a[3] &&
    isBearish(c) && c[3] < ((a[0] + a[3]) / 2)
  ) {
    return isDoji(b) ? 'Evening Doji Star' : 'Evening Star';
  }
  return null;
}

export function detectThreeWhiteSoldiers(ohlc, i) {
  if (i < 2) return null;
  const [a, b, c] = [ohlc[i - 2], ohlc[i - 1], ohlc[i]];
  if (
    isBullish(a) && isBullish(b) && isBullish(c) &&
    b[0] > a[3] && c[0] > b[3] &&
    b[3] > b[0] && c[3] > c[0] &&
    a[3] < b[3] && b[3] < c[3]
  ) {
    return 'Three White Soldiers';
  }
  return null;
}

export function detectThreeBlackCrows(ohlc, i) {
  if (i < 2) return null;
  const [a, b, c] = [ohlc[i - 2], ohlc[i - 1], ohlc[i]];
  if (
    isBearish(a) && isBearish(b) && isBearish(c) &&
    b[0] < a[3] && c[0] < b[3] &&
    b[3] < b[0] && c[3] < c[0] &&
    a[3] > b[3] && b[3] > c[3]
  ) {
    return 'Three Black Crows';
  }
  return null;
}

export function detectDoji(ohlc, i) {
  if (isDoji(ohlc[i])) {
    return 'Doji';
  }
  return null;
}

export function detectHammer(ohlc, i) {
  const bar = ohlc[i];
  if (!bar) return null;
  const body = bodySize(bar);
  const lower = lowerShadow(bar);
  const upper = upperShadow(bar);
  if (
    body < (bar[1] - bar[2]) * 0.3 &&
    lower > body * 2 &&
    upper < body * 0.5 &&
    bar[3] > bar[0]
  ) {
    return 'Hammer';
  }
  return null;
}

export function detectMarubozu(ohlc, i) {
  const bar = ohlc[i];
  if (!bar) return null;
  const body = bodySize(bar);
  const upper = upperShadow(bar);
  const lower = lowerShadow(bar);
  if (
    body > (bar[1] - bar[2]) * 0.9 &&
    upper < body * 0.05 &&
    lower < body * 0.05
  ) {
    return isBullish(bar) ? 'Bullish Marubozu' : 'Bearish Marubozu';
  }
  return null;
}

// Aggregate detector
export function detectAllPatterns(ohlc, i) {
  return [
    detectBearishEngulfing(ohlc, i),
    detectMorningStar(ohlc, i),
    detectEveningStar(ohlc, i),
    detectThreeWhiteSoldiers(ohlc, i),
    detectThreeBlackCrows(ohlc, i),
    detectDoji(ohlc, i),
    detectHammer(ohlc, i),
    detectMarubozu(ohlc, i)
  ].filter(Boolean);
}
